import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTeam } from './TeamContext';

/**
 * Compact team selector dropdown for the navigation bar.
 * Shows current team name with a dropdown to switch teams or manage them.
 */
export default function TeamSelector({ isDark }) {
  const { teams, currentTeam, switchTeam, hasTeams } = useTeam();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (!hasTeams) {
    return (
      <Link
        to="/teams"
        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
        }`}
      >
        Join Team
      </Link>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
        }`}
      >
        <span className="mr-1">🏆</span>
        <span className="max-w-[120px] truncate">{currentTeam?.name || 'Teams'}</span>
        <svg className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className={`absolute right-0 mt-1 w-56 rounded-xl shadow-lg border z-50 py-1 ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          {/* Team list */}
          <div className="px-2 py-1">
            <div className={`text-xs font-medium px-2 py-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Switch Team
            </div>
            {teams.map(team => (
              <button
                key={team.id}
                onClick={() => { switchTeam(team.id); setOpen(false); }}
                className={`w-full text-left px-2 py-1.5 rounded-lg text-sm transition-colors flex items-center justify-between ${
                  team.id === currentTeam?.id
                    ? isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'
                    : isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="truncate">{team.name}</span>
                {team.role === 'admin' && (
                  <span className="text-xs text-purple-400 ml-2 shrink-0">admin</span>
                )}
              </button>
            ))}
          </div>

          {/* Divider + links */}
          <div className={`border-t mx-2 my-1 ${isDark ? 'border-gray-700' : 'border-gray-200'}`} />
          <Link
            to="/leaderboard"
            onClick={() => setOpen(false)}
            className={`block px-4 py-1.5 text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
          >
            Leaderboard
          </Link>
          <Link
            to="/teams"
            onClick={() => setOpen(false)}
            className={`block px-4 py-1.5 text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
          >
            Manage Teams
          </Link>
        </div>
      )}
    </div>
  );
}
